import React, { useState, useEffect } from "react";
import axios from "axios";

export default function AdminQuestionPanel() {
  const [backendUrl, setBackendUrl] = useState("");

  // Courses, exams
  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedExam, setSelectedExam] = useState("");

  // Manual question
  const [questionText, setQuestionText] = useState("");
  const [marks, setMarks] = useState(1);
  const [questionType, setQuestionType] = useState("MCQ");
  const [answers, setAnswers] = useState(["", "", "", ""]);
  const [correctIndex, setCorrectIndex] = useState(null);

  // Master question bank
  const [fileOld, setFileOld] = useState(null);
  const [fileMaster, setFileMaster] = useState(null);
  const [mstQuestions, setMstQuestions] = useState([]);
  const [selectedMasterIds, setSelectedMasterIds] = useState([]);

  // Loading and status for each button
  const [loadingManual, setLoadingManual] = useState(false);
  const [loadingOldExcel, setLoadingOldExcel] = useState(false);
  const [loadingMasterExcel, setLoadingMasterExcel] = useState(false);
  const [loadingAssignMaster, setLoadingAssignMaster] = useState(false);

  const [statusManual, setStatusManual] = useState("");
  const [statusOldExcel, setStatusOldExcel] = useState("");
  const [statusMasterExcel, setStatusMasterExcel] = useState("");
  const [statusAssignMaster, setStatusAssignMaster] = useState("");

// Details of Excel rows skipped during upload
const [skippedExcelRows, setSkippedExcelRows] = useState([]);

  // ---------- Load BACKEND_URL ----------
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/config.js";
    script.onload = () => {
      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      } else console.error(" BACKEND_URL not found in config.js");
    };
    script.onerror = () => console.error(" Failed to load config.js");
    document.body.appendChild(script);
  }, []);

  // ---------- Fetch Courses ----------
  useEffect(() => {
    if (!backendUrl) return;
    axios
      .get(`${backendUrl}/admin/courses`)
      .then((res) => setCourses(res.data))
      .catch((err) => console.error(err));
  }, [backendUrl]);

  // ---------- Fetch Exams ----------
  useEffect(() => {
    if (!backendUrl || !selectedCourse) {
      setExams([]);
      setSelectedExam("");
      return;
    }
    axios
      .get(`${backendUrl}/exams/get-exams/${selectedCourse}`)
      .then((res) => setExams(res.data))
      .catch((err) => console.error(err));
  }, [backendUrl, selectedCourse]);

  // ---------- Fetch Master Questions ----------
  useEffect(() => {
    if (!backendUrl || !selectedCourse) {
      setMstQuestions([]);
      setSelectedMasterIds([]);
      return;
    }
    axios
      .get(`${backendUrl}/admin/mst-questions/${selectedCourse}`)
      .then((res) => setMstQuestions(res.data))
      .catch((err) => console.error(err));
  }, [backendUrl, selectedCourse]);

  // ---------- Manual question helpers ----------
  const resetManualForm = () => {
    setQuestionText("");
    setMarks(1);
    setQuestionType("MCQ");
    setAnswers(["", "", "", ""]);
    setCorrectIndex(null);
  };

  const validateManual = () => {
    if (!selectedCourse || !selectedExam) return "Course and Exam must be selected.";
    if (!questionText.trim()) return "Question text is required.";
    if (marks < 1) return "Marks must be at least 1.";
    if (questionType === "MCQ") {
      if (answers.some((a) => !a.trim())) return "All answers must be filled.";
      if (correctIndex === null) return "Select the correct answer.";
    }
    return null;
  };

  const handleManualSubmit = async () => {
    const error = validateManual();
    if (error) {
      setStatusManual(` ${error}`);
      return;
    }

    setLoadingManual(true);
    setStatusManual("");
    try {
      const courseName = courses.find((c) => c.ID === parseInt(selectedCourse))?.Course_Name;
      const examName = exams.find((e) => e.Exam_ID === parseInt(selectedExam))?.Exam_Name;

      const res = await axios.post(
        `${backendUrl}/exams/manual-entry`,
        {
          questionText,
          questionType,
          marks: parseInt(marks),
          answers,
          correctAnswerIndex: parseInt(correctIndex),
          courseName,
          examName,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setStatusManual(`${res.data.message}`);
      resetManualForm();
    } catch (err) {
      console.error(err);
      setStatusManual(err.response?.data?.message || " Failed to save question.");
    } finally {
      setLoadingManual(false);
    }
  };

  // ---------- Excel uploads ----------
 const handleOldExcelUpload = async () => {
  if (!fileOld) {
    setStatusOldExcel("Please select a file.");
    return;
  }

  if (!selectedCourse || !selectedExam) {
    setStatusOldExcel("Select course and exam.");
    return;
  }

  setLoadingOldExcel(true);
  setStatusOldExcel("");
  setSkippedExcelRows([]);

  const formData = new FormData();

  formData.append("file", fileOld);

  formData.append(
    "courseName",
    courses.find(
      (c) => c.ID === parseInt(selectedCourse)
    )?.Course_Name
  );

  formData.append(
    "examName",
    exams.find(
      (e) => e.Exam_ID === parseInt(selectedExam)
    )?.Exam_Name
  );

  try {
    const res = await axios.post(
      `${backendUrl}/exams/upload-excel`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );

    // Show upload summary
    setStatusOldExcel(
      res.data.message || "Excel upload completed."
    );

    // Store skipped row details
    if (Array.isArray(res.data.skippedRows)) {
      setSkippedExcelRows(res.data.skippedRows);
    } else {
      setSkippedExcelRows([]);
    }

  } catch (err) {
    console.error("Excel upload error:", err);

    setStatusOldExcel(
      err.response?.data?.message ||
      "Old Excel upload failed."
    );

    setSkippedExcelRows([]);
  } finally {
    setLoadingOldExcel(false);
  }
};

  const handleMasterExcelUpload = async () => {
    if (!fileMaster) return setStatusMasterExcel(" Please select a file.");
    if (!selectedCourse) return setStatusMasterExcel(" Please select a course.");
    setLoadingMasterExcel(true);
    setStatusMasterExcel("");

    const formData = new FormData();
    formData.append("file", fileMaster);
    formData.append("courseId", selectedCourse);

    try {
      const res = await axios.post(`${backendUrl}/exams/upload-master-excel`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatusMasterExcel(`${res.data.message}`);

      const mstRes = await axios.get(`${backendUrl}/admin/mst-questions/${selectedCourse}`);
      setMstQuestions(mstRes.data);
      setSelectedMasterIds([]);
    } catch (err) {
      console.error(err);
      setStatusMasterExcel(" Master Excel upload failed.");
    } finally {
      setLoadingMasterExcel(false);
    }
  };

  // ---------- Assign master ----------
  const handleAssignMaster = async () => {
    if (!selectedExam) return setStatusAssignMaster(" Please select an exam.");
    if (!selectedMasterIds.length) return setStatusAssignMaster(" Select at least one master question.");
    setLoadingAssignMaster(true);
    setStatusAssignMaster("");

    try {
      const res = await axios.post(`${backendUrl}/exams/assign-master-questions`, {
        examId: selectedExam,
        masterQuestionIds: selectedMasterIds,
        maxMarks: marks,
      });
      setStatusAssignMaster(`${res.data.message}`);
      setSelectedMasterIds([]);
    } catch (err) {
      console.error(err);
      setStatusAssignMaster(err.response?.data?.message || " Assigning master questions failed.");
    } finally {
      setLoadingAssignMaster(false);
    }
  };

  // Reset answers array if questionType changes
  useEffect(() => {
    if (questionType !== "MCQ") setAnswers([]);
    else if (answers.length !== 4) setAnswers(["", "", "", ""]);
  }, [questionType]);

  // ---------- Styles ----------
  const containerStyle = {
    maxWidth: "1100px",
    margin: "10px auto",
    padding: "30px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    fontFamily: "Roboto, Arial, sans-serif",
    color: "#333",
  };
  const section = { border: "1px solid #ddd", borderRadius: "8px", padding: "20px", marginBottom: "20px" };
  const sectionHeader = { color: "#E30016", fontSize: "17px", fontWeight: "600", marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "8px" };
  const labelStyle = { display: "block", fontWeight: "600", marginBottom: "6px", marginTop: "10px", color: "#444" };
  const inputStyle = { width: "98%", padding: "10px", borderRadius: "3px", border: "1.4px solid #ccc", fontSize: "15px", marginBottom: "10px" };
  const selectStyle = { ...inputStyle };
  const buttonStyle = { width: "30%", padding: "12px", backgroundColor: "#E30016", color: "#fff", border: "none", borderRadius: "6px", fontSize: "16px", fontWeight: "600", cursor: "pointer", marginTop: "10px", transition: "background-color 0.3s", display: "block", margin: "10px auto" };
  const grayButton = { ...buttonStyle, backgroundColor: "#6E6F70" };
  const answerCard = { padding: "12px", border: "1px solid #ccc", borderRadius: "6px", marginBottom: "12px", backgroundColor: "#f9f9f9" };
  const statusStyle = (text) => ({
    marginTop: "15px",
    padding: "12px",
    textAlign: "center",
    borderRadius: "6px",
    fontWeight: "500",
    color: "#3e4041ff",  //text.startsWith("*") ? "#1a7f37" : "#a70000",
    //border: `1px solid ${text.startsWith("*") ? "#1a7f37" : "#a70000"}`,
  });

  return (
    <div style={containerStyle}>
      <h2 style={{ textAlign: "center", color: "#E30016", marginBottom: "30px", fontSize: "20px", fontWeight: "700" }}>
        Question Management Console
      </h2>

      {/* --- Course & Exam Selection --- */}
      <div style={section}>
        <h3 style={sectionHeader}>Choose Course & Exam</h3>
        <label style={labelStyle}>Course</label>
        <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} style={selectStyle}>
          <option value="">-- Select Course --</option>
          {courses.map((c) => <option key={c.ID} value={c.ID}>{c.Course_Name}</option>)}
        </select>

        <label style={labelStyle}>Exam</label>
        <select value={selectedExam} onChange={(e) => setSelectedExam(e.target.value)} style={selectStyle}>
          <option value="">-- Select Exam --</option>
          {exams.map((ex) => <option key={ex.Exam_ID} value={ex.Exam_ID}>{ex.Exam_Name}</option>)}
        </select>
      </div>

      {/* --- Old Excel Upload --- */}
   
      <div style={section}>
        <h3 style={sectionHeader}>📤 Upload Questions via Excel (Direct)</h3>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "15px",
          }}
        >
          {/* Left: File input + Upload button */}
          <div style={{ flex: "1 1 70%", display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="file"
              onChange={(e) => setFileOld(e.target.files[0])}
              style={{
                flex: "1",
                padding: "8px",
                border: "1.5px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
            <button
              onClick={handleOldExcelUpload}
              style={{
                backgroundColor: "#6E6F70",
                color: "#fff",
                fontWeight: "600",
                border: "none",
                borderRadius: "6px",
                padding: "10px 16px",
                cursor: "pointer",
                fontSize: "15px",
                minWidth: "180px",
              }}
              disabled={loadingOldExcel}
            >
              {loadingOldExcel ? "Uploading..." : "Upload Excel File"}
            </button>
          </div>

          {/* Right: View Questions button + text */}
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              whiteSpace: "nowrap",
            }}
          >
            <button
              onClick={() => {
                if (!selectedCourse || !selectedExam) {
                  alert("Please select a course and exam first.");
                  return;
                }
                window.location.href = `/view-questions/${selectedCourse}/${selectedExam}`;

              }}
              style={{
                backgroundColor: "#E30016",
                color: "#fff",
                fontWeight: "600",
                border: "none",
                borderRadius: "6px",
                padding: "10px 16px",
                cursor: "pointer",
                fontSize: "15px",
                minWidth: "180px",
              }}
            >
              View Questions
            </button>
            <p
              style={{
                fontSize: "13px",
                color: "#555",
                margin: 0,
                userSelect: "none",
              }}
            >
              Review Assigned Questions
            </p>
          </div>
        </div>

        {/* Status message below */}
       {statusOldExcel && (
  <div style={statusStyle(statusOldExcel)}>
    {statusOldExcel}
  </div>
)}

{skippedExcelRows.length > 0 && (
  <div
    style={{
      marginTop: "20px",
      padding: "15px",
      border: "1px solid #ddd",
      borderRadius: "6px",
      backgroundColor: "#fff8f8"
    }}
  >
    <h4
      style={{
        color: "#a70000",
        marginTop: 0,
        marginBottom: "12px"
      }}
    >
      Skipped Excel Rows ({skippedExcelRows.length})
    </h4>

    <div
      style={{
        overflowX: "auto",
        maxHeight: "350px",
        overflowY: "auto"
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "13px"
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                border: "1px solid #ccc",
                padding: "8px",
                backgroundColor: "#eee",
                width: "90px"
              }}
            >
              Excel Row
            </th>

            <th
              style={{
                border: "1px solid #ccc",
                padding: "8px",
                backgroundColor: "#eee",
                width: "250px"
              }}
            >
              Reason
            </th>

            <th
              style={{
                border: "1px solid #ccc",
                padding: "8px",
                backgroundColor: "#eee"
              }}
            >
              Question
            </th>
          </tr>
        </thead>

        <tbody>
          {skippedExcelRows.map((row, index) => (
            <tr key={index}>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  textAlign: "center",
                  fontWeight: "600"
                }}
              >
                {row.excelRow ?? "-"}
              </td>

              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "8px"
                }}
              >
                {row.reason ?? "Not specified"}
              </td>

              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "8px"
                }}
              >
                {row.question ?? row.questionText ?? "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}
      </div>


      {/* --- Master Excel Upload --- */}
      <div style={section}>
        <h3 style={sectionHeader}>📤 Upload Questions to Master Bank</h3>
        <input type="file" onChange={(e) => setFileMaster(e.target.files[0])} style={inputStyle} />
        <button onClick={handleMasterExcelUpload} style={grayButton} disabled={loadingMasterExcel}>
          {loadingMasterExcel ? "Uploading..." : "Upload to Master Bank"}
        </button>
        {statusMasterExcel && <div style={statusStyle(statusMasterExcel)}>{statusMasterExcel}</div>}
      </div>

      {/* --- Assign Master Questions --- */}
      <div style={section}>
        <h3 style={sectionHeader}>📋 Assign Master Questions to Exam</h3>
        {mstQuestions.length === 0 && <p>No master questions found for this course.</p>}

        <label style={labelStyle}>Max Marks for Selected Questions</label>
        <input type="number" min={1} value={marks} onChange={e => setMarks(parseInt(e.target.value))} style={inputStyle} />

        {mstQuestions.map(q => (
          <div key={q.Question_ID} style={answerCard}>
            <label>
              <input
                type="checkbox"
                checked={selectedMasterIds.includes(q.Question_ID)}
                onChange={e => {
                  const newSelected = e.target.checked
                    ? [...selectedMasterIds, q.Question_ID]
                    : selectedMasterIds.filter(id => id !== q.Question_ID);
                  setSelectedMasterIds(newSelected);
                }}
                style={{ marginRight: "6px" }}
              />
              {q.Question_Text} ({q.Marks} Marks)
            </label>
          </div>
        ))}

        {selectedMasterIds.length > 0 && (() => {
          const totalSelected = mstQuestions
            .filter(q => selectedMasterIds.includes(q.Question_ID))
            .reduce((sum, q) => sum + q.Marks, 0);

          if (totalSelected > marks) {
            return <div style={{ color: "#a70000", fontWeight: "200", marginBottom: "10px" }}> Total selected marks ({totalSelected}) exceed Max Marks ({marks})</div>;
          } else {
            return <div style={{ color: "#1a7f37", fontWeight: "200", marginBottom: "10px" }}> Total selected marks: {totalSelected} / {marks}</div>;
          }
        })()}

        <button
          onClick={handleAssignMaster}
          style={buttonStyle}
          disabled={loadingAssignMaster || selectedMasterIds.reduce((sum, id) => sum + (mstQuestions.find(q => q.Question_ID === id)?.Marks || 0), 0) > marks}
        >
          {loadingAssignMaster ? "Assigning..." : "Assign Selected to Exam"}
        </button>
        {statusAssignMaster && <div style={statusStyle(statusAssignMaster)}>{statusAssignMaster}</div>}
      </div>

      {/* --- Manual Question Entry --- */}
      <div style={section}>
        <h3 style={sectionHeader}> Add Manual Question</h3>
        <label style={labelStyle}>Question Text</label>
        <textarea rows={3} value={questionText} onChange={e => setQuestionText(e.target.value)} style={{ ...inputStyle, resize: "vertical" }} />
        <label style={labelStyle}>Marks</label>
        <input type="number" min={1} value={marks} onChange={e => setMarks(e.target.value)} style={inputStyle} />
        <label style={labelStyle}>Question Type</label>
        <select value={questionType} onChange={e => setQuestionType(e.target.value)} style={selectStyle}>
          <option value="MCQ">MCQ</option>
          <option value="TrueFalse">True/False</option>
          <option value="FillBlanks">Fill in the Blanks</option>
        </select>

        {questionType === 'MCQ' && answers.map((a, i) => (
          <div key={i} style={answerCard}>
            <label style={labelStyle}>Answer {i + 1}</label>
            <input type="text" value={a} onChange={e => setAnswers(prev => { const upd = [...prev]; upd[i] = e.target.value; return upd; })} style={inputStyle} />
            <label><input type="radio" name="correct" checked={correctIndex === i} onChange={() => setCorrectIndex(i)} style={{ marginRight: "6px" }} />Mark as Correct</label>
          </div>
        ))}

        <button onClick={handleManualSubmit} style={buttonStyle} disabled={loadingManual}>
          {loadingManual ? "Saving..." : "Save Question"}
        </button>
        {statusManual && <div style={statusStyle(statusManual)}>{statusManual}</div>}
      </div>
    </div>
  );
}
